import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon, TicketIcon, CheckIcon, InboxIcon,
  ExclamationTriangleIcon, ArrowUpIcon, ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from 'react-query';
import { notificationService } from '../../services';
import { formatDistanceToNow } from 'date-fns';

const ICON_MAP = {
  ticket_assigned:  { icon: TicketIcon, bg: 'bg-blue-50', color: 'text-blue-600' },
  ticket_escalated: { icon: ArrowUpIcon, bg: 'bg-red-50', color: 'text-red-600' },
  ticket_commented: { icon: ChatBubbleLeftIcon, bg: 'bg-green-50', color: 'text-green-600' },
  ticket_sla_breach:{ icon: ExclamationTriangleIcon, bg: 'bg-orange-50', color: 'text-orange-600' },
};

/* ─── NotificationsDropdown ──────────────────────────────────────────────── */
const NotificationsDropdown = () => {
  const [open, setOpen]       = useState(false);
  const ref                   = useRef(null);
  const navigate              = useNavigate();
  const queryClient           = useQueryClient();

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* fetch real notifications — auto-refresh every 30 s */
  const { data: notifData = {} } = useQuery(
    ['notifications-feed'],
    () => notificationService.getAll({ limit: 15 }).then((r) => r.data),
    { refetchInterval: 30_000, staleTime: 20_000 }
  );

  const { data: countData = {} } = useQuery(
    ['notifications-unread-count'],
    () => notificationService.getUnreadCount().then((r) => r.data?.data),
    { refetchInterval: 30_000, staleTime: 20_000 }
  );

  const notifications = (notifData?.data || []).map((n) => ({
    id:     n._id,
    title:  n.title,
    sub:    n.message,
    time:   n.createdAt,
    type:   n.type,
    path:   n.link || '/tickets',
    isNew:  !n.isRead,
  }));

  const unreadCount = countData?.count || 0;

  const markAllRead = async (e) => {
    e.stopPropagation();
    try {
      await notificationService.markAllAsRead();
      queryClient.invalidateQueries(['notifications-feed']);
      queryClient.invalidateQueries(['notifications-unread-count']);
    } catch { /* ignore */ }
  };

  const handleItemClick = async (notif) => {
    if (notif.isNew) {
      try {
        await notificationService.markAsRead(notif.id);
        queryClient.invalidateQueries(['notifications-feed']);
        queryClient.invalidateQueries(['notifications-unread-count']);
      } catch { /* ignore */ }
    }
    navigate(notif.path);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn-icon btn-ghost relative"
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <BellIcon className="w-5 h-5 text-gray-500" />
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 rounded-full text-[9px] font-bold text-white"
            style={{ background: '#ef4444', padding: '0 3px', border: '2px solid #fff', lineHeight: 1 }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="dropdown-menu right-0 top-full mt-2 animate-scale-in overflow-hidden"
          style={{ width: '320px', minWidth: '320px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #f0f1f5' }}>
            <div className="flex items-center gap-2">
              <BellIcon className="w-4 h-4 text-[#12206b]" />
              <p className="text-sm font-semibold text-gray-900">Notifications</p>
              {unreadCount > 0 && (
                <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-tight">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-[11px] text-[#12206b] hover:text-[#0a1545] font-medium transition-colors"
              >
                <CheckIcon className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <InboxIcon className="w-8 h-8 text-gray-200" />
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const iconCfg = ICON_MAP[n.type] || { icon: TicketIcon, bg: n.isNew ? 'bg-[#e8f7fc]' : 'bg-gray-100', color: n.isNew ? 'text-[#12206b]' : 'text-gray-400' };
                const IconComp = iconCfg.icon;
                return (
                <button
                  key={n.id}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 last:border-0 ${
                    n.isNew ? 'bg-[#e8f7fc]/60 hover:bg-[#e8f7fc]' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleItemClick(n)}
                >
                  {/* Icon */}
                  <div
                    className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconCfg.bg}`}
                  >
                    <IconComp className={`w-4 h-4 ${iconCfg.color}`} />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] leading-snug truncate ${n.isNew ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}>
                      {n.title}
                    </p>
                    <p className="text-[11px] text-gray-400 capitalize mt-0.5">{n.sub}</p>
                    <p className="text-[10px] text-gray-300 mt-1">
                      {formatDistanceToNow(new Date(n.time), { addSuffix: true })}
                    </p>
                  </div>

                  {/* Unread dot */}
                  {n.isNew && (
                    <span className="w-2 h-2 bg-[#00b4d8] rounded-full flex-shrink-0 mt-2" />
                  )}
                </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-2.5 text-center"
            style={{ borderTop: '1px solid #f0f1f5' }}
          >
            <button
              onClick={() => { navigate('/tickets'); setOpen(false); }}
              className="text-xs text-[#12206b] hover:text-[#0a1545] font-medium transition-colors"
            >
              View all tickets →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
